import React from 'react';
import { Text, RichText, Link, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { AdsCard, AdsCardContent } from '@arm/arm-design-system-react';
import './styles.css';
import '../SharedUtils/themeColors/backgroundColor.css';
import { addBackgroundColorVariables } from '../SharedUtils/themeColors';
import RenderingStyling from '../../../lib/RenderingStyling';
import { addStyle, getTrackingAttributes } from '../SharedUtils/utils';
import { useInvertedButton } from '../SharedUtils/hooks/useInvertedButton';
import { useInvertedColor } from '../SharedUtils/hooks/useInvertedColor';

const ImageAboveTextCard = ({ fields, rendering, sitecoreContext }) => {
  const isEditorMode = sitecoreContext.pageEditing;

  const widgetClasses = ['o-widget', 'c-image-above-text-card'];
  const widgetStyles = {};

  addBackgroundColorVariables(fields, widgetStyles);

  const isButtonInverted = useInvertedButton(fields);
  const isColorInverted = useInvertedColor(fields);

  const isTextAlignCentered = fields.TextAlignCentered?.value;

  if (isColorInverted) widgetClasses.push('is-inverted');
  if (isTextAlignCentered) widgetClasses.push('u-text-center');

  const imageStyles = {};
  if (fields.BackgroundImageCover?.value) {
    addStyle(imageStyles, 'backgroundSize', 'cover');
  }

  const imageSrc = fields.Image?.value?.src;
  if (imageSrc) {
    addStyle(imageStyles, 'backgroundImage', `url('${imageSrc}')`);
  }

  const imageALT = fields.Image?.value?.alt || '';
  const imageHref = !isEditorMode && fields.CallToAction?.value?.href;

  const trackingAttributes = getTrackingAttributes(fields);

  return (
    <div {...RenderingStyling(rendering.componentName)} className={widgetClasses.join(' ')}>
      <AdsCard style={widgetStyles} className="background-color-ads-card">
        <AdsCardContent slot="content" className="c-image-above-text-card__content">
          <div
            className={
              'u-flex u-flex-column u-flex-1 u-height-100' +
              (isTextAlignCentered ? ' u-align-items-center' : '')
            }
          >
            {(isEditorMode || imageSrc) && (
              <a
                href={imageHref}
                className="c-image-above-text-card__link"
                title={fields.PrimaryHeading?.value}
              >
                <div
                  className="c-image-above-text-card__image"
                  style={imageStyles}
                  title={imageALT}
                  aria-label={imageALT}
                  tabIndex="0"
                  data-component="Image"
                />
              </a>
            )}

            {(isEditorMode || fields.PrimaryHeading?.value) && (
              <h3
                tabIndex="0"
                className="c-image-above-text-card__primary u-text-lg-3 u-text-md-3 u-text-sm-3 u-text-bold-500"
                data-component="Primary"
              >
                <Text field={fields.PrimaryHeading} />
              </h3>
            )}

            {fields.UseHorizontalRule?.value && (
              <div className="c-image-above-text-card__divider"></div>
            )}

            {(isEditorMode || fields.SecondaryHeading?.value) && (
              <h4
                tabIndex="0"
                className="c-image-above-text-card__secondary u-text-lg-4 u-text-md-4 u-text-sm-4"
                data-component="Secondary"
              >
                <Text field={fields.SecondaryHeading} />
              </h4>
            )}

            {(isEditorMode || fields.Text?.value) && (
              <div
                tabIndex="0"
                className="c-image-above-text-card__text u-text-body-lg u-text-body-md u-text-body-sm"
                data-component="Text"
              >
                <RichText field={fields.Text} />
              </div>
            )}

            {(isEditorMode || fields.CallToAction?.value?.href) && (
              <div className="c-image-above-text-card__cta">
                <Link
                  field={fields.CallToAction}
                  className={
                    'c-button c-cta-button ' + (isButtonInverted ? 'is-secondary' : 'is-primary')
                  }
                  role="button"
                  {...trackingAttributes}
                />
              </div>
            )}
          </div>
        </AdsCardContent>
      </AdsCard>
    </div>
  );
};

export default withSitecoreContext()(ImageAboveTextCard);
